import { useState, useEffect, useRef, useCallback } from 'react';
import { getReports, putReport, deleteReport, getUnsyncedReports, addToSyncQueue } from './db';
import { syncReportToSharePoint, registerBackgroundSync, onBackgroundSyncMessage, initMsal } from './sharepoint';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const SETTINGS_KEY = 'expensio_settings';
const USER_KEY = 'expensio_user';

const DEFAULT_SETTINGS = {
  adminPassword: 'admin1234',
  expenseTypes: ['Travel', 'Meals', 'Accommodation', 'Software', 'Equipment', 'Client Entertainment', 'Other'],
  currencies: ['USD', 'EUR', 'GBP', 'BRL', 'CAD'],
  projects: ['Project Alpha', 'Project Beta', 'Internal', 'Unassigned'],
  companyName: 'My Company',
  azureClientId: '',
  azureTenantId: '',
  sharepointSiteId: '',
  sharepointFolderId: '',
};

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const today = () => new Date().toISOString().split('T')[0];
const fmt = (amount, currency) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount || 0);

function loadSettings() {
  try { return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}') }; }
  catch { return DEFAULT_SETTINGS; }
}
function saveSettings(s) { localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)); }
function loadUser() {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); }
  catch { return null; }
}
function saveUser(u) { localStorage.setItem(USER_KEY, JSON.stringify(u)); }

// ─── ICONS ────────────────────────────────────────────────────────────────────
const I = ({ n, s = 18, c = 'currentColor' }) => {
  const d = {
    home: 'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z',
    plus: 'M12 5v14M5 12h14',
    settings: 'M12 15a3 3 0 100-6 3 3 0 000 6z M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z',
    cloud: 'M16 16l-4-4-4 4M12 12v9M20.4 18.4A5 5 0 0018 9h-1.26A8 8 0 103 16.3',
    check: 'M20 6L9 17l-5-5',
    trash: 'M3 6h18M8 6V4h8v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6',
    lock: 'M7 11V7a5 5 0 0110 0v4M3 11h18v11H3z',
    wifi: 'M1.42 9a16 16 0 0121.16 0M5 12.55a11 11 0 0114.08 0M8.53 16.11a6 6 0 016.95 0M12 20h.01',
    wifiOff: 'M1 1l22 22M16.72 11.06A11 11 0 0119 12.55M5 12.55a11 11 0 015.17-2.39M10.71 5.05A16 16 0 0122.56 9M1.42 9a16 16 0 014.7-2.88M8.53 16.11a6 6 0 016.95 0M12 20h.01',
    back: 'M19 12H5M12 19l-7-7 7-7',
    edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
    x: 'M18 6L6 18M6 6l12 12',
    user: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 3a4 4 0 110 8 4 4 0 010-8z',
    camera: 'M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2zM12 17a4 4 0 110-8 4 4 0 010 8z',
    receipt: 'M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1zM8 7h8M8 11h8M8 15h4',
    dollar: 'M12 1v22M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 010 7H6',
    info: 'M12 2a10 10 0 110 20A10 10 0 0112 2zM12 16v-4M12 8h.01',
    refresh: 'M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.36-3.36L23 10M1 14l5.13 4.36A9 9 0 0020.49 15',
  };
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {(d[n] || '').split('M').filter(Boolean).map((p, i) => <path key={i} d={`M${p}`} />)}
    </svg>
  );
};

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Playfair+Display:wght@700;800&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; -webkit-tap-highlight-color: transparent; }
    html { font-size: 16px; }
    body { font-family: 'Outfit', sans-serif; background: #F4F1ED; color: #1C1917; overscroll-behavior: none; }
    input, select, textarea, button { font-family: 'Outfit', sans-serif; font-size: 16px; }
    ::-webkit-scrollbar { width: 0; height: 0; }
    ::-webkit-input-placeholder { color: #B0A89E; }
    @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
    @keyframes fadeUp { from{opacity:0;transform:translateX(-50%) translateY(10px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }
    @keyframes spin { to{transform:rotate(360deg)} }
    @keyframes slideUp { from{transform:translateY(100%)} to{transform:translateY(0)} }
    .anim { animation: fadeIn .25s ease both; }
    .slide-up { animation: slideUp .3s cubic-bezier(.16,1,.3,1) both; }
    .spin { animation: spin .7s linear infinite; display:inline-block; }

    /* Buttons */
    .btn { border:none; border-radius:12px; padding:12px 20px; font-size:15px; font-weight:600; cursor:pointer; transition:all .15s; display:inline-flex; align-items:center; justify-content:center; gap:7px; -webkit-appearance:none; }
    .btn:active { transform:scale(.97); }
    .btn-dark { background:#1C1917; color:#fff; }
    .btn-dark:disabled { background:#B0A89E; cursor:not-allowed; }
    .btn-outline { background:transparent; border:1.5px solid #DDD7CE; color:#555; }
    .btn-outline:hover { border-color:#1C1917; color:#1C1917; }
    .btn-danger { background:#FEE2E2; color:#DC2626; }
    .btn-success { background:#D1FAE5; color:#065F46; }
    .btn-sm { padding:8px 14px; font-size:13px; border-radius:9px; }

    /* Form fields */
    .field { margin-bottom:14px; }
    .field label { display:block; font-size:11px; font-weight:700; letter-spacing:.07em; text-transform:uppercase; color:#9C8F84; margin-bottom:5px; }
    .field input, .field select, .field textarea {
      width:100%; border:1.5px solid #DDD7CE; border-radius:11px;
      padding:12px 14px; font-size:16px; background:#fff; outline:none;
      transition:border .2s; color:#1C1917; -webkit-appearance:none; appearance:none;
    }
    .field select { background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239C8F84' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 14px center; padding-right:36px; }
    .field input:focus, .field select:focus, .field textarea:focus { border-color:#1C1917; }
    .field.err input, .field.err select { border-color:#DC2626; }
    .field .errtxt { color:#DC2626; font-size:12px; margin-top:4px; }
    .autofilled { border-color:#7C3AED !important; background:#FAF8FF !important; }

    /* Cards */
    .card { background:#fff; border-radius:16px; border:1px solid #E8E3DC; }
    .pressable { transition:all .15s; cursor:pointer; }
    .pressable:active { transform:scale(.98); opacity:.9; }

    /* Toggle switch */
    .toggle-wrap { display:flex; align-items:center; justify-content:space-between; padding:13px 0; border-bottom:1px solid #F0EBE3; }
    .toggle { position:relative; width:44px; height:26px; flex-shrink:0; }
    .toggle input { opacity:0; width:0; height:0; position:absolute; }
    .t-track { position:absolute; inset:0; background:#DDD7CE; border-radius:26px; cursor:pointer; transition:.25s; }
    .t-thumb { position:absolute; width:20px; height:20px; top:3px; left:3px; background:#fff; border-radius:50%; transition:.25s; box-shadow:0 1px 3px rgba(0,0,0,.2); }
    .toggle input:checked ~ .t-track { background:#1C1917; }
    .toggle input:checked ~ .t-thumb { transform:translateX(18px); }

    /* Nav bar */
    .nav-bar { position:fixed; bottom:0; left:0; right:0; background:#fff; border-top:1px solid #E8E3DC; display:flex; z-index:200; padding-bottom:env(safe-area-inset-bottom); }
    .nav-btn { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:10px 0; border:none; background:transparent; cursor:pointer; color:#B0A89E; gap:3px; font-size:9px; font-weight:700; letter-spacing:.05em; text-transform:uppercase; transition:color .15s; }
    .nav-btn.active { color:#1C1917; }
    .nav-btn:active { opacity:.7; }

    /* Chips */
    .chip { display:inline-flex; align-items:center; padding:3px 9px; border-radius:20px; font-size:11px; font-weight:700; white-space:nowrap; }

    /* Progress */
    .prog-track { height:7px; background:#EDE8E0; border-radius:8px; overflow:hidden; }
    .prog-fill { height:100%; border-radius:8px; transition:width .6s cubic-bezier(.16,1,.3,1); }

    /* Header */
    .top-bar { background:#fff; border-bottom:1px solid #E8E3DC; padding:14px 20px; display:flex; align-items:center; justify-content:space-between; position:sticky; top:0; z-index:100; }
    .page-content { padding:18px 18px 100px; }

    /* Toast */
    .toast { position:fixed; bottom:calc(72px + env(safe-area-inset-bottom) + 8px); left:50%; transform:translateX(-50%); padding:12px 22px; border-radius:40px; font-size:14px; font-weight:600; z-index:999; animation:fadeUp .25s ease; white-space:nowrap; box-shadow:0 4px 20px rgba(0,0,0,.2); }
  `}</style>
);

// ─── TOGGLE COMPONENT ────────────────────────────────────────────────────────
const Toggle = ({ label, sub, value, onChange }) => (
  <div className="toggle-wrap">
    <div>
      <div style={{ fontSize: 15, fontWeight: 500 }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: '#9C8F84', marginTop: 1 }}>{sub}</div>}
    </div>
    <label className="toggle">
      <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} />
      <div className="t-track" />
      <div className="t-thumb" />
    </label>
  </div>
);

// ─── APP ROOT ─────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(loadUser);
  const [settings, setSettings] = useState(loadSettings);
  const [reports, setReports] = useState([]);
  const [view, setView] = useState('dashboard');
  const [activeReport, setActiveReport] = useState(null);
  const [editExpense, setEditExpense] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState({});
  const [toast, setToast] = useState(null);
  const [settingsUnlocked, setSettingsUnlocked] = useState(false);

  // Register PWA service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then(reg => {
        console.log('SW registered:', reg.scope);
      }).catch(e => console.log('SW reg failed:', e));
    }
    // Init MSAL if credentials exist
    if (settings.azureClientId && settings.azureTenantId) {
      // Load MSAL dynamically
      const script = document.createElement('script');
      script.src = 'https://alcdn.msauth.net/browser/2.38.0/js/msal-browser.min.js';
      script.onload = () => initMsal(settings.azureClientId, settings.azureTenantId);
      document.head.appendChild(script);
    }
  }, [settings.azureClientId, settings.azureTenantId]);

  // Online/offline
  useEffect(() => {
    const on = () => { setOnline(true); autoSync(); };
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  });

  // Background sync message from SW
  useEffect(() => {
    onBackgroundSyncMessage(autoSync);
  }, []);

  // Load reports
  useEffect(() => {
    if (user) {
      getReports(user.id).then(setReports).catch(() => {});
    }
  }, [user]);

  const showToast = useCallback((msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const saveReport = useCallback(async (report) => {
    await putReport(report);
    setReports(prev => {
      const i = prev.findIndex(r => r.id === report.id);
      if (i >= 0) { const n = [...prev]; n[i] = report; return n; }
      return [report, ...prev];
    });
  }, []);

  const removeReport = useCallback(async (id) => {
    await deleteReport(id);
    setReports(prev => prev.filter(r => r.id !== id));
  }, []);

  const syncReport = useCallback(async (report) => {
    setSyncStatus(s => ({ ...s, [report.id]: 'syncing' }));
    try {
      const result = await syncReportToSharePoint(report, settings);
      const updated = { ...report, synced: true, syncedAt: result.syncedAt, sharePointUrl: result.fileUrl };
      await saveReport(updated);
      setSyncStatus(s => ({ ...s, [report.id]: 'synced' }));
      showToast('Synced to SharePoint ✓');
    } catch (e) {
      await addToSyncQueue(report.id);
      await registerBackgroundSync();
      setSyncStatus(s => ({ ...s, [report.id]: 'error' }));
      showToast(e.message || 'Sync failed — queued for retry', 'err');
    }
  }, [settings, saveReport, showToast]);

  const autoSync = useCallback(async () => {
    if (!user) return;
    const unsynced = await getUnsyncedReports(user.id);
    for (const r of unsynced) await syncReport(r).catch(() => {});
  }, [user, syncReport]);

  const nav = useCallback((v, data = {}) => {
    setView(v);
    if (data.report !== undefined) setActiveReport(data.report);
    if (data.expense !== undefined) setEditExpense(data.expense);
    window.scrollTo(0, 0);
  }, []);

  if (!user) return (
    <>
      <GlobalStyles />
      <LoginScreen onLogin={u => { saveUser(u); setUser(u); }} />
    </>
  );

  const currentReport = activeReport ? (reports.find(r => r.id === activeReport.id) || activeReport) : null;

  return (
    <>
      <GlobalStyles />

      {/* Status bar */}
      <div style={{ background: '#1C1917', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px', paddingTop: 'calc(10px + env(safe-area-inset-top))' }}>
        <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 800 }}>{settings.companyName}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: online ? 1 : 0.5 }}>
            <I n={online ? 'wifi' : 'wifiOff'} s={13} c="#fff" />
            <span style={{ fontSize: 11 }}>{online ? 'Online' : 'Offline'}</span>
          </span>
          <span style={{ opacity: 0.4 }}>·</span>
          <span style={{ opacity: 0.6, fontSize: 11 }}>{user.name}</span>
        </div>
      </div>

      {/* Views */}
      <div style={{ minHeight: 'calc(100vh - 44px)', paddingBottom: 'calc(72px + env(safe-area-inset-bottom))' }}>
        {view === 'dashboard' && (
          <Dashboard reports={reports} settings={settings} syncStatus={syncStatus} online={online}
            onOpen={r => nav('report', { report: r })} onNew={() => nav('new-report')} onSync={syncReport} />
        )}
        {view === 'new-report' && (
          <ReportForm settings={settings} userId={user.id} userName={user.name}
            onSave={async r => { await saveReport(r); nav('report', { report: r }); showToast('Report created'); }}
            onCancel={() => nav('dashboard')} />
        )}
        {view === 'report' && currentReport && (
          <ReportDetail
            report={currentReport} settings={settings} syncStatus={syncStatus} online={online}
            onAddExpense={() => nav('expense-form', { expense: null })}
            onEditExpense={e => nav('expense-form', { expense: e })}
            onDeleteExpense={async expId => {
              const updated = { ...currentReport, expenses: currentReport.expenses.filter(e => e.id !== expId), synced: false };
              await saveReport(updated); nav('report', { report: updated }); showToast('Removed');
            }}
            onUpdateReport={async fields => {
              const updated = { ...currentReport, ...fields, synced: false };
              await saveReport(updated); nav('report', { report: updated });
            }}
            onSync={syncReport}
            onDelete={async () => { await removeReport(currentReport.id); nav('dashboard'); showToast('Deleted'); }}
            onBack={() => nav('dashboard')}
          />
        )}
        {view === 'expense-form' && currentReport && (
          <ExpenseForm
            expense={editExpense} settings={settings}
            onSave={async expense => {
              const expenses = editExpense
                ? currentReport.expenses.map(e => e.id === expense.id ? expense : e)
                : [...(currentReport.expenses || []), expense];
              const updated = { ...currentReport, expenses, synced: false };
              await saveReport(updated);
              nav('report', { report: updated });
              showToast(editExpense ? 'Updated' : 'Expense added');
            }}
            onCancel={() => nav('report', { report: currentReport })}
          />
        )}
        {view === 'settings' && (
          <SettingsPage
            settings={settings} unlocked={settingsUnlocked}
            onUnlock={pw => { if (pw === settings.adminPassword) { setSettingsUnlocked(true); return true; } return false; }}
            onSave={s => { saveSettings(s); setSettings(s); showToast('Settings saved'); }}
          />
        )}
      </div>

      {/* Bottom nav */}
      <nav className="nav-bar">
        {[
          { id: 'dashboard', icon: 'home', label: 'Home' },
          { id: 'new-report', icon: 'plus', label: 'New' },
          { id: 'settings', icon: 'settings', label: 'Settings' },
          { id: '__logout', icon: 'user', label: 'Logout' },
        ].map(n => (
          <button key={n.id} className={`nav-btn ${view === n.id ? 'active' : ''}`}
            onClick={() => n.id === '__logout' ? (saveUser(null), setUser(null)) : nav(n.id)}>
            <I n={n.icon} s={21} />
            {n.label}
          </button>
        ))}
      </nav>

      {/* Toast */}
      {toast && (
        <div className="toast" style={{ background: toast.type === 'err' ? '#DC2626' : '#1C1917', color: '#fff' }}>
          {toast.msg}
        </div>
      )}
    </>
  );
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const submit = () => {
    if (!name.trim()) { setErr('Enter your name'); return; }
    onLogin({ id: name.trim().toLowerCase().replace(/\s+/g, '_') + '_' + Date.now(), name: name.trim() });
  };
  return (
    <div style={{ minHeight: '100vh', background: '#1C1917', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 360 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 42, fontWeight: 800, color: '#fff' }}>expensio</div>
          <div style={{ color: '#9C8F84', fontSize: 14, marginTop: 6 }}>Offline · Sync when ready</div>
        </div>
        <div style={{ background: '#fff', borderRadius: 22, padding: 28 }}>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 18 }}>Who's logging in?</div>
          <div className="field">
            <label>Your Full Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Maria Silva"
              onKeyDown={e => e.key === 'Enter' && submit()} autoFocus />
            {err && <div className="errtxt">{err}</div>}
          </div>
          <button className="btn btn-dark" style={{ width: '100%', padding: 14, fontSize: 16 }} onClick={submit}>
            Start Tracking →
          </button>
          <div style={{ marginTop: 14, fontSize: 12, color: '#B0A89E', textAlign: 'center', lineHeight: 1.5 }}>
            Data is saved locally on your device.<br />Sync to SharePoint when you're online.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({ reports, settings, syncStatus, online, onOpen, onNew, onSync }) {
  const total = reports.reduce((s, r) => s + (r.expenses || []).reduce((a, e) => a + e.amount, 0), 0);
  const unsynced = reports.filter(r => !r.synced).length;
  return (
    <div className="anim">
      <div className="top-bar">
        <div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 800 }}>Dashboard</div>
          <div style={{ fontSize: 12, color: '#9C8F84', marginTop: 1 }}>{reports.length} reports · {unsynced > 0 ? `${unsynced} pending sync` : 'All synced ✓'}</div>
        </div>
        <button className="btn btn-dark btn-sm" onClick={onNew}><I n="plus" s={14} /> New</button>
      </div>
      <div className="page-content">
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
          <div className="card" style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9C8F84', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 5 }}>Total Logged</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 800 }}>{fmt(total, settings.currencies[0])}</div>
          </div>
          <div className="card" style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9C8F84', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 5 }}>Reports</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 800 }}>{reports.length}</div>
            {unsynced > 0 && <div style={{ fontSize: 11, color: '#D97706', marginTop: 2, fontWeight: 600 }}>↑ {unsynced} to sync</div>}
          </div>
        </div>

        {reports.length === 0 && (
          <div style={{ textAlign: 'center', padding: '52px 0', color: '#B0A89E' }}>
            <I n="receipt" s={40} c="#DDD7CE" />
            <div style={{ fontWeight: 600, marginTop: 12, marginBottom: 4 }}>No reports yet</div>
            <div style={{ fontSize: 13 }}>Tap New to create your first expense report</div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {reports.map(r => {
            const total = (r.expenses || []).reduce((s, e) => s + e.amount, 0);
            const balance = r.cashAdvance - total;
            const pct = r.cashAdvance > 0 ? Math.min(100, (total / r.cashAdvance) * 100) : 0;
            const st = syncStatus[r.id];
            return (
              <div key={r.id} className="card pressable" style={{ padding: '15px 16px' }} onClick={() => onOpen(r)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ flex: 1, marginRight: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: '#9C8F84', marginTop: 2 }}>{r.project} · {(r.expenses || []).length} items · {r.currency}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 18 }}>{fmt(total, r.currency)}</div>
                    <span className="chip" style={{ background: r.synced ? '#D1FAE5' : '#FEF3C7', color: r.synced ? '#065F46' : '#92400E', marginTop: 4 }}>
                      {st === 'syncing' ? '⏳ Syncing' : r.synced ? '✓ Synced' : '● Local'}
                    </span>
                  </div>
                </div>
                {r.cashAdvance > 0 && (
                  <>
                    <div className="prog-track" style={{ marginBottom: 5 }}>
                      <div className="prog-fill" style={{ width: `${pct}%`, background: pct > 90 ? '#EF4444' : pct > 70 ? '#F59E0B' : '#10B981' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9C8F84' }}>
                      <span>Advance: {fmt(r.cashAdvance, r.currency)}</span>
                      <span style={{ fontWeight: 700, color: balance < 0 ? '#DC2626' : '#059669' }}>
                        {balance < 0 ? `Over ${fmt(Math.abs(balance), r.currency)}` : `${fmt(balance, r.currency)} left`}
                      </span>
                    </div>
                  </>
                )}
                {!r.synced && online && st !== 'syncing' && (
                  <button className="btn btn-success btn-sm" style={{ width: '100%', marginTop: 10 }}
                    onClick={e => { e.stopPropagation(); onSync(r); }}>
                    <I n="cloud" s={13} /> Sync to SharePoint
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── REPORT FORM ──────────────────────────────────────────────────────────────
function ReportForm({ settings, userId, userName, onSave, onCancel }) {
  const [f, sf] = useState({ name: '', project: settings.projects[0], currency: settings.currencies[0], cashAdvance: '', notes: '' });
  const [errs, setErrs] = useState({});
  const set = (k, v) => sf(p => ({ ...p, [k]: v }));
  const submit = () => {
    const e = {};
    if (!f.name.trim()) e.name = 'Required';
    if (Object.keys(e).length) { setErrs(e); return; }
    onSave({ id: uid(), userId, userName, ...f, cashAdvance: parseFloat(f.cashAdvance) || 0, expenses: [], createdAt: new Date().toISOString(), synced: false });
  };
  return (
    <div className="anim">
      <div className="top-bar">
        <button className="btn btn-outline btn-sm" onClick={onCancel}><I n="back" s={15} /></button>
        <div style={{ fontWeight: 700 }}>New Report</div>
        <div style={{ width: 60 }} />
      </div>
      <div className="page-content">
        <div className={`field ${errs.name ? 'err' : ''}`}>
          <label>Report Name *</label>
          <input value={f.name} onChange={e => set('name', e.target.value)} placeholder="e.g. NYC Client Trip — April" autoFocus />
          {errs.name && <div className="errtxt">{errs.name}</div>}
        </div>
        <div className="field">
          <label>Project</label>
          <select value={f.project} onChange={e => set('project', e.target.value)}>
            {settings.projects.map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label>Currency</label>
            <select value={f.currency} onChange={e => set('currency', e.target.value)}>
              {settings.currencies.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Cash Advance</label>
            <input type="number" min="0" step="0.01" placeholder="0.00" value={f.cashAdvance} onChange={e => set('cashAdvance', e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label>Notes</label>
          <textarea rows={2} value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="Trip purpose, client name..." style={{ resize: 'none' }} />
        </div>
        <button className="btn btn-dark" style={{ width: '100%', padding: 14 }} onClick={submit}>Create Report →</button>
      </div>
    </div>
  );
}

// ─── REPORT DETAIL ────────────────────────────────────────────────────────────
function ReportDetail({ report, settings, syncStatus, online, onAddExpense, onEditExpense, onDeleteExpense, onUpdateReport, onSync, onDelete, onBack }) {
  const exps = report.expenses || [];
  const total = exps.reduce((s, e) => s + e.amount, 0);
  const billable = exps.filter(e => e.billable).reduce((s, e) => s + e.amount, 0);
  const reimbursable = exps.filter(e => e.reimbursable).reduce((s, e) => s + e.amount, 0);
  const balance = report.cashAdvance - total;
  const pct = report.cashAdvance > 0 ? Math.min(100, (total / report.cashAdvance) * 100) : 0;
  const st = syncStatus[report.id];
  const [editAdv, setEditAdv] = useState(false);
  const [advVal, setAdvVal] = useState(String(report.cashAdvance || 0));
  const [generatingPdf, setGeneratingPdf] = useState(false);

  async function generatePDF() {
    setGeneratingPdf(true);
    try {
      // Load jsPDF dynamically
      if (!window.jspdf) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
          s.onload = resolve; s.onerror = reject;
          document.head.appendChild(s);
        });
      }
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const W = 210; const H = 297;
      const margin = 15;
      let y = margin;

      // ── Header ──────────────────────────────────────────────────────────────
      doc.setFillColor(22, 48, 41);
      doc.rect(0, 0, W, 28, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('WYSUPP', margin, 12);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Staff Expense Report', margin, 19);
      doc.setFontSize(8);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, W - margin, 19, { align: 'right' });
      y = 36;

      // ── Report info ──────────────────────────────────────────────────────────
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      const infoItems = [
        ['Report:', report.name || ''],
        ['Consultant:', report.userName || ''],
        ['Project:', report.project || ''],
        ['Currency:', report.currency || 'USD'],
        ['Cash Advance:', fmt(report.cashAdvance || 0, report.currency)],
        ['Total Expenses:', fmt(total, report.currency)],
        ['Balance:', fmt(balance, report.currency)],
      ];
      const col2Start = W / 2 + 5;
      infoItems.forEach((item, i) => {
        const col = i < 4 ? margin : col2Start;
        const row = i < 4 ? i : i - 4;
        const yy = y + row * 6;
        doc.setFont('helvetica', 'bold');
        doc.text(item[0], col, yy);
        doc.setFont('helvetica', 'normal');
        doc.text(String(item[1]), col + 30, yy);
      });
      y += 28;

      // ── Divider ──────────────────────────────────────────────────────────────
      doc.setDrawColor(33, 182, 125);
      doc.setLineWidth(0.5);
      doc.line(margin, y, W - margin, y);
      y += 6;

      // ── Expense table header ─────────────────────────────────────────────────
      doc.setFillColor(22, 48, 41);
      doc.rect(margin, y, W - margin * 2, 7, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      const cols = [margin + 2, 40, 80, 110, 133, 153, 168, 185];
      const headers = ['#', 'Date', 'Description', 'Type', 'Currency', 'Amount', 'Billable', 'Reimb.'];
      headers.forEach((h, i) => doc.text(h, cols[i], y + 5));
      y += 10;

      // ── Expense rows ─────────────────────────────────────────────────────────
      doc.setTextColor(30, 30, 30);
      doc.setFont('helvetica', 'normal');
      exps.forEach((exp, idx) => {
        if (y > H - 40) { doc.addPage(); y = margin; }
        const bg = idx % 2 === 0;
        if (bg) {
          doc.setFillColor(245, 248, 246);
          doc.rect(margin, y - 3, W - margin * 2, 7, 'F');
        }
        doc.setFontSize(7);
        doc.text(String(idx + 1), cols[0], y + 2);
        doc.text(exp.date || '', cols[1], y + 2);
        const title = exp.title || '';
        doc.text(title.length > 22 ? title.substring(0, 22) + '…' : title, cols[2], y + 2);
        doc.text(exp.type || '', cols[3], y + 2);
        doc.text(exp.currency || report.currency || '', cols[4], y + 2);
        doc.setFont('helvetica', 'bold');
        doc.text(exp.amount?.toFixed(2) || '0.00', cols[5], y + 2);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(exp.billable ? 22 : 150, exp.billable ? 120 : 150, exp.billable ? 60 : 150);
        doc.text(exp.billable ? 'Yes' : 'No', cols[6], y + 2);
        doc.setTextColor(exp.reimbursable ? 22 : 150, exp.reimbursable ? 120 : 150, exp.reimbursable ? 60 : 150);
        doc.text(exp.reimbursable ? 'Yes' : 'No', cols[7], y + 2);
        doc.setTextColor(30, 30, 30);
        y += 7;
      });

      // ── Summary row ──────────────────────────────────────────────────────────
      y += 3;
      doc.setDrawColor(33, 182, 125);
      doc.line(margin, y, W - margin, y);
      y += 5;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(22, 48, 41);
      doc.text(`Total Expenses: ${fmt(total, report.currency)}`, margin, y);
      doc.text(`Billable: ${fmt(billable, report.currency)}`, margin + 65, y);
      doc.text(`Reimbursable: ${fmt(reimbursable, report.currency)}`, margin + 120, y);
      y += 6;
      doc.setTextColor(balance < 0 ? 180 : 33, balance < 0 ? 30 : 130, balance < 0 ? 30 : 60);
      doc.text(`Balance (Advance - Total): ${fmt(balance, report.currency)}`, margin, y);
      y += 10;

      // ── Receipt photos ───────────────────────────────────────────────────────
      const receipts = exps.filter(e => e.receipt && e.receipt.startsWith('data:image'));
      if (receipts.length > 0) {
        doc.addPage();
        y = margin;
        doc.setFillColor(22, 48, 41);
        doc.rect(0, 0, W, 20, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('RECEIPT PHOTOS', margin, 13);
        y = 28;

        for (let i = 0; i < receipts.length; i++) {
          const exp = receipts[i];
          if (y > H - 80) { doc.addPage(); y = margin; }

          // Expense label
          doc.setTextColor(22, 48, 41);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.text(`${i + 1}. ${exp.title || 'Receipt'} — ${exp.date || ''} — ${fmt(exp.amount, exp.currency || report.currency)}`, margin, y);
          y += 4;

          // Receipt image
          try {
            const imgData = exp.receipt;
            const format = imgData.includes('png') ? 'PNG' : 'JPEG';
            const imgW = W - margin * 2;
            const imgH = 80;
            doc.addImage(imgData, format, margin, y, imgW, imgH, '', 'FAST');
            y += imgH + 8;
          } catch(e) {
            doc.setTextColor(150, 50, 50);
            doc.setFontSize(8);
            doc.text('[Image could not be rendered]', margin, y + 5);
            y += 15;
          }

          doc.setDrawColor(200, 224, 214);
          doc.setLineWidth(0.3);
          doc.line(margin, y - 3, W - margin, y - 3);
        }
      }

      // ── Footer on all pages ──────────────────────────────────────────────────
      const pageCount = doc.getNumberOfPages();
      for (let p = 1; p <= pageCount; p++) {
        doc.setPage(p);
        doc.setFillColor(22, 48, 41);
        doc.rect(0, H - 10, W, 10, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text('Wysupp — Confidential Expense Report', margin, H - 4);
        doc.text(`Page ${p} of ${pageCount}`, W - margin, H - 4, { align: 'right' });
      }

      // ── Download ──────────────────────────────────────────────────────────────
      const safeName = (report.name || 'Report').replace(/[^a-zA-Z0-9]/g, '_');
      const date = new Date().toISOString().split('T')[0];
      doc.save(`${date}_${report.userName || 'Consultant'}_${safeName}.pdf`);
    } catch(err) {
      console.error('PDF error:', err);
      alert('Could not generate PDF: ' + err.message);
    } finally {
      setGeneratingPdf(false);
    }
  }

  return (
    <div className="anim">
      <div className="top-bar">
        <button className="btn btn-outline btn-sm" onClick={onBack}><I n="back" s={15} /></button>
        <div style={{ fontWeight: 700, fontSize: 14, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{report.name}</div>
        <button className="btn btn-danger btn-sm" onClick={() => confirm('Delete this report?') && onDelete()}><I n="trash" s={14} /></button>
      </div>
      <div className="page-content">
        {/* Hero summary card */}
        <div style={{ background: '#1C1917', color: '#fff', borderRadius: 20, padding: '22px 22px 18px', marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.4, letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 4 }}>{report.project} · {report.currency}</div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 34, fontWeight: 800, marginBottom: 14 }}>{fmt(total, report.currency)}</div>
          {report.cashAdvance > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ height: 5, background: 'rgba(255,255,255,.15)', borderRadius: 5, overflow: 'hidden', marginBottom: 7 }}>
                <div style={{ height: '100%', borderRadius: 5, background: pct > 90 ? '#F87171' : pct > 70 ? '#FBBF24' : '#34D399', width: `${pct}%`, transition: 'width .6s' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, opacity: 0.65 }}>
                <span>Advance: {fmt(report.cashAdvance, report.currency)}</span>
                <span style={{ color: balance < 0 ? '#F87171' : '#34D399', fontWeight: 700 }}>
                  {balance < 0 ? `Over ${fmt(Math.abs(balance), report.currency)}` : `${fmt(balance, report.currency)} left`}
                </span>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <span className="chip" style={{ background: 'rgba(255,255,255,.1)', color: '#fff', fontSize: 12 }}>Billable {fmt(billable, report.currency)}</span>
            <span className="chip" style={{ background: 'rgba(255,255,255,.1)', color: '#fff', fontSize: 12 }}>Reimb. {fmt(reimbursable, report.currency)}</span>
          </div>
        </div>

        {/* Cash advance editor */}
        <div className="card" style={{ padding: '13px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9C8F84', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 3 }}>Cash Advance</div>
            {editAdv
              ? <input type="number" value={advVal} onChange={e => setAdvVal(e.target.value)} autoFocus
                  style={{ border: 'none', outline: 'none', fontSize: 20, fontWeight: 700, width: 130, padding: 0, background: 'transparent' }} />
              : <div style={{ fontSize: 20, fontWeight: 700 }}>{fmt(report.cashAdvance, report.currency)}</div>
            }
          </div>
          {editAdv
            ? <button className="btn btn-dark btn-sm" onClick={() => { onUpdateReport({ cashAdvance: parseFloat(advVal) || 0 }); setEditAdv(false); }}>Save</button>
            : <button className="btn btn-outline btn-sm" onClick={() => setEditAdv(true)}><I n="edit" s={14} /></button>
          }
        </div>

        {/* Sync button */}
        {report.synced ? (
          <div style={{ background: '#D1FAE5', borderRadius: 12, padding: '11px 15px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#065F46', fontWeight: 600 }}>
            <I n="check" s={15} c="#065F46" /> Synced to SharePoint {report.syncedAt ? `· ${new Date(report.syncedAt).toLocaleDateString()}` : ''}
            {report.sharePointUrl && <a href={report.sharePointUrl} target="_blank" rel="noreferrer" style={{ marginLeft: 'auto', fontSize: 12, color: '#065F46' }}>Open ↗</a>}
          </div>
        ) : online ? (
          <button className="btn btn-success" style={{ width: '100%', marginBottom: 12 }} onClick={() => onSync(report)} disabled={st === 'syncing'}>
            {st === 'syncing'
              ? <><span className="spin" style={{ width: 15, height: 15, border: '2px solid #ccc', borderTopColor: '#065F46', borderRadius: '50%' }} /> Syncing...</>
              : <><I n="cloud" s={15} /> Sync to SharePoint</>}
          </button>
        ) : (
          <div style={{ background: '#FEF3C7', borderRadius: 12, padding: '11px 15px', marginBottom: 12, fontSize: 13, color: '#92400E', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <I n="wifiOff" s={14} c="#92400E" /> Offline — will sync automatically when connected
          </div>
        )}

        {/* Generate Report PDF */}
        <button
          className="btn"
          style={{ width: '100%', marginBottom: 12, background: '#163029', color: '#fff', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, letterSpacing: '0.05em', textTransform: 'uppercase' }}
          onClick={generatePDF}
          disabled={generatingPdf || exps.length === 0}
        >
          {generatingPdf
            ? <><span className="spin" style={{ width: 15, height: 15, border: '2px solid #ccc', borderTopColor: '#21B67D', borderRadius: '50%' }} /> Generating...</>
            : <><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6}}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg> Generate Report PDF</>
          }
        </button>

        {/* Expenses */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Expenses ({exps.length})</div>
          <button className="btn btn-dark btn-sm" onClick={onAddExpense}><I n="plus" s={14} /> Add</button>
        </div>

        {exps.length === 0 && (
          <div style={{ textAlign: 'center', padding: '36px 0', color: '#B0A89E' }}>
            <I n="receipt" s={36} c="#DDD7CE" />
            <div style={{ marginTop: 10, fontSize: 14 }}>No expenses yet — tap Add</div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {exps.map(exp => (
            <div key={exp.id} className="card" style={{ padding: '13px 15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, marginRight: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{exp.title}</div>
                  <div style={{ fontSize: 12, color: '#9C8F84', marginTop: 2, marginBottom: 6 }}>{exp.type} · {exp.date}</div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {exp.billable && <span className="chip" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>Billable</span>}
                    {exp.reimbursable && <span className="chip" style={{ background: '#FEF3C7', color: '#92400E' }}>Reimb.</span>}
                    <span className="chip" style={{ background: '#F3F4F6', color: '#374151' }}>{exp.project}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 17 }}>{fmt(exp.amount, exp.currency)}</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-outline btn-sm" style={{ padding: '6px 10px' }} onClick={() => onEditExpense(exp)}><I n="edit" s={13} /></button>
                    <button className="btn btn-danger btn-sm" style={{ padding: '6px 10px' }} onClick={() => confirm('Remove expense?') && onDeleteExpense(exp.id)}><I n="trash" s={13} /></button>
                  </div>
                </div>
              </div>
              {exp.receipt && (
                <img src={exp.receipt} alt="receipt" style={{ width: '100%', maxHeight: 130, objectFit: 'cover', borderRadius: 8, marginTop: 10, border: '1px solid #E8E3DC' }} />
              )}
              {exp.notes ? <div style={{ fontSize: 12, color: '#9C8F84', marginTop: 8, paddingTop: 8, borderTop: '1px solid #F0EBE3' }}>{exp.notes}</div> : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── EXPENSE FORM ─────────────────────────────────────────────────────────────
function ExpenseForm({ expense, settings, onSave, onCancel }) {
  const init = expense || { id: uid(), title: '', type: settings.expenseTypes[0], project: settings.projects[0], currency: settings.currencies[0], amount: '', date: today(), billable: false, reimbursable: false, notes: '', receipt: null };
  const [f, sf] = useState(init);
  const [errs, setErrs] = useState({});
  const [scanning, setScanning] = useState(false);
  const [scanDone, setScanDone] = useState(false);
  const fileRef = useRef();
  const set = (k, v) => sf(p => ({ ...p, [k]: v }));




async function handleReceipt(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      set('receipt', dataUrl);
      if (!file.type.startsWith('image/')) return;
      setScanning(true);
      try {
        const compressImage = (dataUrl) => {
          return new Promise(resolve => {
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              const maxSize = 800;
              let w = img.width, h = img.height;
              if (w > maxSize) { h = h * maxSize / w; w = maxSize; }
              if (h > maxSize) { w = w * maxSize / h; h = maxSize; }
              canvas.width = w; canvas.height = h;
              canvas.getContext('2d').drawImage(img, 0, 0, w, h);
              resolve(canvas.toDataURL('image/jpeg', 0.7).split(',')[1]);
            };
            img.src = dataUrl;
          });
        };
        const b64 = await compressImage(dataUrl);
        const fetchWithRetry = async (retries = 3) => {
          const res = await fetch('https://api.anthropic.com/v1/messages', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': process.env.REACT_APP_ANTHROPIC_KEY,
              'anthropic-version': '2023-06-01',
              'anthropic-dangerous-direct-browser-access': 'true',
            },
            body: JSON.stringify({
              model: 'claude-haiku-4-5-20251001',
              max_tokens: 300,
              messages: [{
                role: 'user',
                content: [
                  { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: b64 } },
                  { type: 'text', text: `Extract receipt info. JSON only, no markdown: {"title":"merchant name","amount":number,"date":"YYYY-MM-DD","category":"one of: ${settings.expenseTypes.join(', ')}","notes":"brief detail"}` }
                ]
              }]
            })
          });
          const data = await res.json();
          if (data.error?.type === 'overloaded_error' && retries > 0) {
            await new Promise(r => setTimeout(r, 3000));
            return fetchWithRetry(retries - 1);
          }
          return data;
        };
        const data = await fetchWithRetry();
        const text = data.content?.find(b => b.type === 'text')?.text || '{}';
        const ex = JSON.parse(text.replace(/```json|```/g, '').trim());
        sf(p => ({
          ...p,
          title: ex.title || p.title,
          amount: ex.amount ? String(ex.amount) : p.amount,
          date: ex.date || p.date,
          type: settings.expenseTypes.includes(ex.category) ? ex.category : p.type,
          notes: ex.notes || p.notes,
        }));
        setScanDone(true);
      } catch (err) {
        console.log('Scan error:', err);
      } finally {
        setScanning(false);
      }
    };
    reader.readAsDataURL(file);
  }
    
  const submit = () => {
    const e = {};
    if (!f.title.trim()) e.title = 'Required';
    if (!f.amount || isNaN(f.amount) || +f.amount <= 0) e.amount = 'Enter valid amount';
    if (!f.date) e.date = 'Required';
    if (Object.keys(e).length) { setErrs(e); return; }
    onSave({ ...f, amount: parseFloat(f.amount), id: f.id || uid() });
  };

  return (
    <div className="anim">
      <div className="top-bar">
        <button className="btn btn-outline btn-sm" onClick={onCancel}><I n="back" s={15} /></button>
        <div style={{ fontWeight: 700 }}>{expense ? 'Edit Expense' : 'New Expense'}</div>
        <div style={{ width: 60 }} />
      </div>
      <div className="page-content">
        {/* Receipt */}
        <div className="field" style={{ marginBottom: 18 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            Receipt
            {scanning && <span style={{ fontSize: 10, color: '#7C3AED', fontWeight: 700, textTransform: 'none', letterSpacing: 0 }}>● Scanning…</span>}
            {scanDone && !scanning && <span style={{ fontSize: 10, color: '#059669', fontWeight: 700, textTransform: 'none', letterSpacing: 0 }}>✓ Auto-filled</span>}
          </label>
          {f.receipt ? (
            <div style={{ position: 'relative' }}>
              <img src={f.receipt} alt="receipt" style={{ width: '100%', maxHeight: 170, objectFit: 'cover', borderRadius: 12, border: scanDone ? '2px solid #7C3AED' : '1.5px solid #DDD7CE' }} />
              <button onClick={() => { set('receipt', null); setScanDone(false); }}
                style={{ position: 'absolute', top: 8, right: 8, background: '#1C1917', color: '#fff', border: 'none', borderRadius: '50%', width: 26, height: 26, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <I n="x" s={13} c="#fff" />
              </button>
              {scanning && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(124,58,237,.1)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ background: '#fff', borderRadius: 12, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 16px rgba(0,0,0,.12)' }}>
                    <span className="spin" style={{ width: 14, height: 14, border: '2px solid #D8B4FE', borderTopColor: '#7C3AED', borderRadius: '50%' }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#7C3AED' }}>Reading receipt…</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div onClick={() => fileRef.current.click()}
              style={{ border: '2px dashed #DDD7CE', borderRadius: 14, padding: '24px 16px', textAlign: 'center', cursor: 'pointer', background: '#FAFAF8' }}>
              <I n="camera" s={28} c="#B0A89E" />
              <div style={{ fontWeight: 600, fontSize: 14, color: '#555', marginTop: 8 }}>Tap to snap or upload receipt</div>
              <div style={{ fontSize: 12, color: '#B0A89E', marginTop: 4 }}>AI will read it & fill the form</div>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleReceipt} />
            </div>
          )}
        </div>

        <div style={{ height: 1, background: '#EDE8E0', marginBottom: 16 }} />

        {scanDone && (
          <div style={{ background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 11, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#5B21B6', display: 'flex', gap: 8 }}>
            <span>✦</span><span>Purple fields were auto-filled by AI — review before submitting.</span>
          </div>
        )}

        <div className={`field ${errs.title ? 'err' : ''}`}>
          <label>Description *</label>
          <input value={f.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Uber to airport" className={scanDone && f.title ? 'autofilled' : ''} />
          {errs.title && <div className="errtxt">{errs.title}</div>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className={`field ${errs.amount ? 'err' : ''}`}>
            <label>Amount *</label>
            <input type="number" min="0" step="0.01" placeholder="0.00" value={f.amount} onChange={e => set('amount', e.target.value)} className={scanDone && f.amount ? 'autofilled' : ''} />
            {errs.amount && <div className="errtxt">{errs.amount}</div>}
          </div>
          <div className="field">
            <label>Currency</label>
            <select value={f.currency} onChange={e => set('currency', e.target.value)}>
              {settings.currencies.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label>Type</label>
            <select value={f.type} onChange={e => set('type', e.target.value)} className={scanDone ? 'autofilled' : ''}>
              {settings.expenseTypes.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className={`field ${errs.date ? 'err' : ''}`}>
            <label>Date *</label>
            <input type="date" value={f.date} onChange={e => set('date', e.target.value)} className={scanDone && f.date ? 'autofilled' : ''} />
          </div>
        </div>

        <div className="field">
          <label>Project</label>
          <select value={f.project} onChange={e => set('project', e.target.value)}>
            {settings.projects.map(p => <option key={p}>{p}</option>)}
          </select>
        </div>

        <div style={{ background: '#F9F6F2', borderRadius: 14, padding: '2px 16px', marginBottom: 14 }}>
          <Toggle label="Billable to client" sub="Will be charged to client invoice" value={f.billable} onChange={v => set('billable', v)} />
          <Toggle label="Reimbursable" sub="Employee will be reimbursed" value={f.reimbursable} onChange={v => set('reimbursable', v)} />
        </div>

        <div className="field">
          <label>Notes</label>
          <textarea rows={2} value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="Extra details..." style={{ resize: 'none' }} className={scanDone && f.notes ? 'autofilled' : ''} />
        </div>

        <button className="btn btn-dark" style={{ width: '100%', padding: 14 }} onClick={submit} disabled={scanning}>
          {expense ? 'Update Expense' : 'Add Expense'} →
        </button>
      </div>
    </div>
  );
}

// ─── SETTINGS ─────────────────────────────────────────────────────────────────
function SettingsPage({ settings, unlocked, onUnlock, onSave }) {
  const [pw, setPw] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [s, setS] = useState(settings);
  const [newItem, setNewItem] = useState({ expenseTypes: '', currencies: '', projects: '' });
  const [tab, setTab] = useState('types');
  const [newPw, setNewPw] = useState('');

  if (!unlocked) return (
    <div className="anim" style={{ padding: 24 }}>
      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 800, marginBottom: 4 }}>Settings</div>
      <div style={{ fontSize: 13, color: '#9C8F84', marginBottom: 28 }}>Admin access required</div>
      <div className="card" style={{ padding: 28, textAlign: 'center' }}>
        <I n="lock" s={36} c="#9C8F84" />
        <div style={{ fontWeight: 700, fontSize: 17, margin: '14px 0 18px' }}>Admin Password</div>
        <input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Enter password"
          style={{ width: '100%', border: '1.5px solid #DDD7CE', borderRadius: 11, padding: '12px 14px', fontSize: 18, outline: 'none', textAlign: 'center', letterSpacing: '.15em', marginBottom: 10 }}
          onKeyDown={e => e.key === 'Enter' && (!onUnlock(pw) && setPwErr('Wrong password'))} />
        {pwErr && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 10 }}>{pwErr}</div>}
        <button className="btn btn-dark" style={{ width: '100%', padding: 13 }} onClick={() => { if (!onUnlock(pw)) setPwErr('Wrong password'); }}>
          Unlock →
        </button>
        <div style={{ marginTop: 12, fontSize: 12, color: '#B0A89E' }}>Default: admin1234</div>
      </div>
    </div>
  );

  const add = (key) => {
    const v = newItem[key].trim();
    if (!v || s[key].includes(v)) return;
    setS(p => ({ ...p, [key]: [...p[key], v] }));
    setNewItem(p => ({ ...p, [key]: '' }));
  };
  const remove = (key, v) => setS(p => ({ ...p, [key]: p[key].filter(x => x !== v) }));

  const ListEditor = ({ k, ph }) => (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input value={newItem[k]} onChange={e => setNewItem(p => ({ ...p, [k]: e.target.value }))} placeholder={ph}
          style={{ flex: 1, border: '1.5px solid #DDD7CE', borderRadius: 11, padding: '11px 14px', fontSize: 15, outline: 'none' }}
          onKeyDown={e => e.key === 'Enter' && add(k)} />
        <button className="btn btn-dark" style={{ padding: '11px 16px' }} onClick={() => add(k)}><I n="plus" s={16} /></button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {s[k].map(item => (
          <div key={item} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F9F6F2', borderRadius: 11, padding: '11px 14px' }}>
            <span style={{ fontSize: 15, fontWeight: 500 }}>{item}</span>
            <button className="btn btn-danger btn-sm" style={{ padding: '5px 10px' }} onClick={() => remove(k, item)}><I n="x" s={13} /></button>
          </div>
        ))}
      </div>
    </>
  );

  const tabs = [{ id: 'types', label: 'Types' }, { id: 'currencies', label: 'Currencies' }, { id: 'projects', label: 'Projects' }, { id: 'sync', label: 'Sync' }, { id: 'general', label: 'General' }];

  return (
    <div className="anim">
      <div className="top-bar">
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 800 }}>Settings</div>
        <button className="btn btn-dark btn-sm" onClick={() => onSave(s)}>Save</button>
      </div>
      <div className="page-content">
        {/* Tab bar */}
        <div style={{ display: 'flex', background: '#EDE8E0', borderRadius: 13, padding: 4, marginBottom: 20, gap: 3, overflowX: 'auto' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ flex: '0 0 auto', padding: '8px 14px', border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all .18s', background: tab === t.id ? '#fff' : 'transparent', color: tab === t.id ? '#1C1917' : '#9C8F84', boxShadow: tab === t.id ? '0 1px 4px rgba(0,0,0,.1)' : 'none' }}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'types' && <><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Expense Types</div><ListEditor k="expenseTypes" ph="Add new type..." /></>}
        {tab === 'currencies' && <><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Currencies</div><ListEditor k="currencies" ph="e.g. JPY" /></>}
        {tab === 'projects' && <><div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Projects</div><ListEditor k="projects" ph="Add project..." /></>}

        {tab === 'sync' && (
          <>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Microsoft SharePoint Sync</div>
            <div style={{ fontSize: 13, color: '#9C8F84', marginBottom: 18, lineHeight: 1.6 }}>
              Configure your Azure App Registration to enable sync. Reports will be uploaded as CSV/Excel files to your SharePoint document library.
            </div>
            <div style={{ background: '#FEF3C7', borderRadius: 12, padding: '12px 14px', marginBottom: 18, fontSize: 13, color: '#92400E', lineHeight: 1.6 }}>
              <strong>Setup steps:</strong><br />
              1. Go to portal.azure.com → App Registrations<br />
              2. Create app, add SPA redirect URI (your app URL)<br />
              3. Add permissions: Files.ReadWrite, Sites.ReadWrite.All<br />
              4. Copy Client ID & Tenant ID below
            </div>
            <div className="field">
              <label>Azure Client ID</label>
              <input value={s.azureClientId} onChange={e => setS(p => ({ ...p, azureClientId: e.target.value }))} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
            </div>
            <div className="field">
              <label>Azure Tenant ID</label>
              <input value={s.azureTenantId} onChange={e => setS(p => ({ ...p, azureTenantId: e.target.value }))} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
            </div>
            <div className="field">
              <label>SharePoint Site ID (optional)</label>
              <input value={s.sharepointSiteId} onChange={e => setS(p => ({ ...p, sharepointSiteId: e.target.value }))} placeholder="yourcompany.sharepoint.com,site-id,web-id" />
            </div>
            <div className="field">
              <label>SharePoint Drive/Folder ID (optional)</label>
              <input value={s.sharepointFolderId} onChange={e => setS(p => ({ ...p, sharepointFolderId: e.target.value }))} placeholder="Leave blank to use OneDrive fallback" />
            </div>
          </>
        )}

        {tab === 'general' && (
          <>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>General</div>
            <div className="field">
              <label>Company Name</label>
              <input value={s.companyName} onChange={e => setS(p => ({ ...p, companyName: e.target.value }))} />
            </div>
            <div style={{ height: 1, background: '#EDE8E0', margin: '18px 0' }} />
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Change Admin Password</div>
            <div className="field">
              <label>New Password (min 6 chars)</label>
              <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="New password" />
            </div>
            <button className="btn btn-outline" onClick={() => {
              if (newPw.trim().length >= 6) { setS(p => ({ ...p, adminPassword: newPw })); setNewPw(''); alert('Password updated — tap Save to apply.'); }
              else alert('Minimum 6 characters');
            }}>Update Password</button>
          </>
        )}
      </div>
    </div>
  );
}
